import * as Yup from 'yup'
import { FormikErrors, yupToFormErrors } from 'formik'
import { ESG_FINAL_CHECK_TABS, INSTITUTIONAL_READINESS_TABS, PROJECT_COMMUNICATION_TABS, STEP_TAB_IDS, STEP_TAB_TITLES, SUMMARY_OF_OPTIONS_TABS } from './_models'

const requiredString = (label: string) =>
    Yup.string()
        .trim()
        .required()
        .label(label)

const requiredNumber = (label: string) =>
    Yup.number()
        .required()
        .label(label)

// An empty number input ('') reads as "not entered" rather than "not a number".
const emptyAsUndefined = (value: unknown, original: unknown) => (original === '' ? undefined : value)

const requiredNumberInput = (label: string) =>
    Yup.number()
        .transform(emptyAsUndefined)
        .typeError(`${label} must be a number`)
        .required(`${label} is required`)
        .min(0, `${label} cannot be less than 0`)

// Required only in the given analysis mode; the other mode's fields are hidden and not checked.
const modeRequiredNumber = (mode: 'cba' | 'cea', label: string) =>
    Yup.number()
        .transform(emptyAsUndefined)
        .when("analysis_mode_selector", {
            is: mode,
            then: () => requiredNumberInput(label),
            otherwise: (schema) => schema.nullable().notRequired(),
        });

// Steps that save each tab on its own, so only the open tab is validated. Keyed by step, then tab id.
export const stepTabSchemas: Partial<Record<string, Record<number, Yup.AnyObjectSchema>>> = {
    esg_final_check: {
        [ESG_FINAL_CHECK_TABS.final_check]: Yup.object({
            gender_responsive_impact_compliance: requiredString('Gender-Responsive Impact Compliance'),
            social_benefit_index: requiredString('Social Benefit Index'),
            environmental_compliance_status: requiredString('Environmental Compliance Status'),
            sustainability_certification_status: requiredString('Sustainability Certification Status'),
        }),
        [ESG_FINAL_CHECK_TABS.eia_summary]: Yup.object({
            overall_eia_summary_and_environmental_authorisation_status: requiredString('Overall EIA Summary & Environmental Authorisation Status'),
            key_environmental_impact_findings: requiredString('Key Environmental Impact Findings'),
            key_social_impact_findings: requiredString('Key Social Impact Findings'),
            gender_impact_findings: requiredString('Gender Impact Findings'),
            mitigation_measures_empr_summary: requiredString('Mitigation Measures EMPR Summary'),
            residual_impacts_acceptability: requiredString('Residual Impacts Acceptability'),
            environmental_authorisation_conditions_compliance_status: requiredString('Environmental Authorisation Conditions Compliance Status'),
        }),
        [ESG_FINAL_CHECK_TABS.documents]: Yup.object({}),
    },
    project_communication_and_stakeholder_engagement_plan: {
        [PROJECT_COMMUNICATION_TABS.project_communication]: Yup.object({
            investor_engagement_strategies: requiredString('Investor Engagement Strategies'),
            community_participation: requiredString('Community Participation'),
            government_regulatory_interactions: requiredString('Government Regulatory Interactions'),
            collaboration_partner_strategy: requiredString('Collaboration Partner Strategy'),
            public_interest_considerations: requiredString('Public Interest Considerations'),
        }),
        [PROJECT_COMMUNICATION_TABS.stakeholder_map]: Yup.object({}),
        [PROJECT_COMMUNICATION_TABS.documents]: Yup.object({}),
    },
    summary_of_option_considered: {
        [SUMMARY_OF_OPTIONS_TABS.base_case]: Yup.object({
            base_case_description: requiredString("Base Case Description"),
        }),
        [SUMMARY_OF_OPTIONS_TABS.options_considered]: Yup.object({}),
        [SUMMARY_OF_OPTIONS_TABS.preferred_option]: Yup.object({
            // No option chosen loads as 0.
            preferred_option: requiredNumber('Preferred Option').positive('Preferred Option is required'),
            preferred_option_government_objectives_justification: requiredString("Preferred Option Government Objectives Justification"),
            value_for_money_conclusion: requiredString('Value for Money Conclusion'),
            supporting_documentation_reference: requiredString('Supporting Documentation Reference'),
        }),
        [SUMMARY_OF_OPTIONS_TABS.documents]: Yup.object({}),
    },
    institutional_and_operational_readiness: {
        [INSTITUTIONAL_READINESS_TABS.readiness]: Yup.object({
            institutional_and_operational_readiness_assessment: requiredString('Institutional & Operational Readiness Assessment'),
        }),
        // Optional fields.
        [INSTITUTIONAL_READINESS_TABS.transformation]: Yup.object({}),
        [INSTITUTIONAL_READINESS_TABS.documents]: Yup.object({}),
    },
}

// The whole step, for the stage-level schema.
const allTabsSchema = (stepKey: string): Yup.AnyObjectSchema =>
    Object.values(stepTabSchemas[stepKey] ?? {}).reduce(
        (schema, tabSchema) => schema.concat(tabSchema),
        Yup.object(),
    )

// The first tab of a tab-wise step, in display order, that fails validation, with its errors.
export async function findIncompleteStepTab(stepKey: string, values: object): Promise<{ tabId: number; title: string; errors: FormikErrors<object> } | null> {
    for (const tabId of STEP_TAB_IDS[stepKey] ?? []) {
        const schema = stepTabSchemas[stepKey]?.[tabId]
        if (!schema) continue
        try {
            await schema.validate(values, { abortEarly: false })
        } catch (error) {
            if (error instanceof Yup.ValidationError) {
                return { tabId, title: STEP_TAB_TITLES[stepKey]?.[tabId] ?? 'every tab', errors: yupToFormErrors(error) }
            }
            throw error
        }
    }
    return null
}

export const conceptAndViabilityStageSchema = {
    final_feasibility_validation: Yup.object({
        feasibility_status: requiredString('Feasibility Status'),
        key_constraints_summary: Yup.string(),
    }),
    cost_benefit_final_model: Yup.object({
        analysis_mode_selector: Yup.string().oneOf(['cba', 'cea']).required().label('Analysis Mode Selector'),
        total_project_cost: requiredNumberInput('Total Project Cost'),
        total_economic_benefit: modeRequiredNumber('cba', 'Total Economic Benefit'),
        cost_per_outcome_unit: modeRequiredNumber('cea', 'Cost per Outcome Unit'),
        total_outcome_units_delivered: modeRequiredNumber('cea', 'Total Outcome Units Delivered'),
    }),
    fiscal_sustainability_ifsa: Yup.object({
        final_fiscal_affordability_index: requiredNumberInput('Fiscal Affordability Index').max(100, 'Fiscal Affordability Index cannot exceed 100'),
        final_contingent_liability_exposure: requiredString('Contingent Liability Exposure'),
        long_term_fiscal_impact_score: requiredNumberInput('Long-term Fiscal Impact Score').max(100, 'Long-term Fiscal Impact Score cannot exceed 100'),
    }),
    risk_finalisation_engine: Yup.object({}),
    long_term_asset_management: Yup.object({
        expenditure_statement: requiredString("Expenditure Statement"),
        funding_statement: requiredString("Funding Statement"),
        cashflow_statement: requiredString("Cashflow Statement"),
        contingent_liability_statement: requiredString("Contingent Liability Statement"),
    }),
    final_investment_decision_engine_core_output: Yup.object({}),
    // The whole step; the form uses stepTabSchemas for the open tab.
    esg_final_check: allTabsSchema('esg_final_check'),
    infrastructure_master_plan: Yup.object({
        master_plan_definition: requiredString('Master Plan Definition'),
    }),
    // The whole step; the form uses stepTabSchemas for the open tab.
    institutional_and_operational_readiness: allTabsSchema('institutional_and_operational_readiness'),
    supervision_reporting_and_evaluation: Yup.object({
        supervision_framework: requiredString('Supervision Framework'),
        evaluation_framework: requiredString('Evaluation Framework'),
        reporting_framework: requiredString('Reporting Framework'),
        me_framework_kpi_data_source: requiredString('M&E Framework — KPIs & Data Sources'),
        me_responsibility_feedback_loops: requiredString('M&E Responsibility & Feedback Loops'),
        reporting_frequency_format: requiredString('Reporting Frequency Format'),
        evaluation_schedule: requiredString('Evaluation Schedule'),
        incentives_for_successful_delivery: requiredString('Incentives for Successful Delivery'),
        penalties_for_non_performance: requiredString('Panelties for Non-Performance'),
    }),
    // The whole step; the form uses stepTabSchemas for the open tab.
    project_communication_and_stakeholder_engagement_plan: allTabsSchema('project_communication_and_stakeholder_engagement_plan'),
    digital_technology_leverage: Yup.object({
        digital_technology_leverage: requiredString('Digital Technology Leverage'),
    }),
    economic_and_commercial_viability: Yup.object({
        value_for_money_financial_assessment: requiredString('Value for Money'),
        summary_financial_vfm_findings: requiredString('Summary of SG1 & SG2 Financial VfM Findings'),
        benefit_analysis_financial_quantification: requiredString('Benefit Analysis'),
        summary_financial_benefit_findings: requiredString('Summary of SG1 & SG2 Financial Benefit Findings'),
        risk_evaluation_financial_risk_costing: requiredString('Risk Evaluation'),
        summary_financial_risk_findings: requiredString('Summary of SG1 & SG2 Risk Findings'),
        economic_multipliers_financial_returns: requiredString('Economic Multipliers'),
        summary_economic_multiplier_financial_findings: requiredString('Summary of SG1 & SG2 Economic Multiplier Financial Findings'),
        economies_of_scale_whole_life_cost_savings: requiredString('Economies of Scale'),
        summary_economies_of_scale_financial_findings: requiredString('Summary of SG1 & SG2 Economies of Scale Financial Findings'),
        cost_effectiveness_unit_cost_metrics: requiredString('Cost-Effectiveness Analysis'),
        summary_cea_financial_findings: requiredString('Summary of SG1 & SG2 CEA Financial Findings'),
        social_economic_financial_monetisation: requiredString('Social & Economic Analysis'),
        summary_social_economic_financial_findings: requiredString('Summary of SG1 & SG2 Social & Economic Financial Findings'),
    }),
    implementation_strategy: Yup.object({
        implementation_strategy_overview: requiredString("Implementation Strategy"),
        asisa_investment_framework_alignment: requiredString(
            "ASISA Investment Framework Alignment"
        ),
        esg_policy_statement_integration_approach: requiredString(
            "ESG Policy Statement & Integration Approach"
        ),
        esg_environmental: requiredString("E — Environmental"),
        esg_social: requiredString("S — Social"),
        esg_governance: requiredString("G — Governance"),
        institutional_investor_funding_mobilisation_strategy: requiredString(
            "Institutional Investor Funding Mobilisation Strategy"
        ),
        blended_finance_structure: requiredString(
            "Blended Finance Structure"
        ),
        institutional_investor_participation_requirements: requiredString(
            "Institutional Investor Participation & Requirements"
        ),
        due_diligence_framework_standards: requiredString(
            "Due Diligence Framework & Standards"
        ),
        impact_reporting_disclosure_commitments: requiredString(
            "Impact Reporting & Disclosure Commitments"
        ),
        impact_measurement_framework: requiredString(
            "Impact Measurement Framework"
        ),
        asisa_disclosure_framework: requiredString(
            "Disclosure Framework"
        ),
        stewardship_arrangements_crisa_alignment: requiredString(
            "Stewardship Arrangements & CRISA Alignment"
        ),
        asisa_risk_management_framework: requiredString(
            "Risk Management Framework"
        ),
        implementation_plan_phasing_schedule: requiredString(
            "Implementation Plan & Phasing Schedule"
        ),
        implementation_monitoring_evaluation: requiredString(
            "Monitoring & Evaluation"
        ),
        exit_strategy_liquidity_provision: requiredString(
            "Exit Strategy & Liquidity Provision"
        ),
    }),
    risk_statement: Yup.object({
        risk_statement_problem_statement: requiredString("Problem Statement & Justification for Investment"),
        risk_statement_risk_context: requiredString("Risk Context & Risk Environment"),
        sensitivity_analysis_overview: requiredString("Sensitivity Analysis Overview & Methodology"),
        sensitivity_analysis_conclusion: requiredString("Sensitivity Analysis Conclusions"),
        overall_risk_statement: requiredString("Overall Risk Statement & Investment Risk Acceptance"),
    }),

    economic_multiplier_and_macroeconomics_benefits: Yup.object({
        // Section 1: GDP Contribution & Economic Growth
        gdp_contribution_estimate: requiredString("GDP Contribution Estimate"),
        regional_economic_growth_effects: requiredString("Regional Economic Growth Effects"),

        // Section 2: Employment Multipliers
        direct_employment: requiredString("Direct Employment"),
        indirect_employment: requiredString("Indirect Employment"),
        induced_employment: requiredString("Induced Employment"),

        // Section 3: Sector Multiplier & Supply Chain Effects
        sector_economic_multiplier: requiredString("Sector Economic Multiplier"),
        supply_chain_local_economic_development: requiredString("Supply Chain & Local Economic Development"),
        productivity_trade_facilitation: requiredString("Productivity & Trade Facilitation"),

        // Section 4: Social Returns & Fiscal Benefits
        poverty_reduction_equity_impact: requiredString("Poverty Reduction & Equity Impact"),
        fiscal_returns_to_government: requiredString("Fiscal Returns to Government"),
        sdg_macroeconomic_policy_alignment: requiredString("SDG & Macroeconomic Policy Alignment"),

        // Section 5: Macroeconomic Risk Factors
        macroeconomic_risks_sensitivity: requiredString("Macroeconomic Risks & Sensitivity"),
    }),
    financial_requirements_and_funding_strategy: Yup.object({
        cost_breakdown: Yup.string(),
        funding_model: requiredString("Funding Model"),
        revenue_strategy: requiredString("Revenue Strategy"),
    }),
    // The whole step; the form uses stepTabSchemas for the open tab.
    summary_of_option_considered: allTabsSchema('summary_of_option_considered'),

    documents: Yup.object({}),
}

export type IConceptAndViabilityStageSchema =
    keyof typeof conceptAndViabilityStageSchema